// MailboxPairAllowlistManager — admin page at /settings/mailbox-pair-allowlist.
// Drives rule-008 (anomalous_mailbox_access) suppression. Read-open, admin-write.
// Mirrors AitmTrustedPairsManager shape (resize/paginate/inline-edit) and adds:
//   - sort state (AiTM source has none today)
//   - 4-option actor_type dropdown (upn / app_id / app_name / *)
//   - "Preview match shape" line
//   - "Reconcile sentinel" admin button
//   - page-level pending-sync banner (sentinel-health poll)

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ArrowsRightLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import {
  fetchMailboxPairAllowlist,
  fetchMailboxPairAllowlistSentinelHealth,
  addMailboxPairAllowlistEntry,
  updateMailboxPairAllowlistEntry,
  removeMailboxPairAllowlistEntry,
  reconcileMailboxPairAllowlistSentinel,
  fetchTenants,
  fetchDetectionRules,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Badge from './ui/Badge';
import { actorTypeTone } from './ui/badgeTones';
import Pagination from './Pagination';

// Rule-008 GUID — the original consumer; kept as the graceful-degradation
// fallback when the detection-rules list is unavailable (e.g. non-admin viewer).
// The set of allowlist-enabled rules is otherwise discovered dynamically from
// each rule's skip_if_in_mailbox_pair_allowlist toggle (v1.70.0).
const RULE_008_ID = 'rule_1066145e-1984-4f06-aff2-49b07f0ff80d';
const RULE_008_FALLBACK = { ruleId: RULE_008_ID, ruleName: 'Anomalous Mailbox Access (rule-008)' };
const UPN_REGEX = /^[^@\s|]+@[^@\s|]+\.[^@\s|]+$/;
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Soft SID pattern per handoff rule 5 — used as a typo-catcher warning only; not a blocker.
const SID_REGEX = /^s-1-\d+-\d+(?:-\d+)*$/i;

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
}

function actorTypeLabel(t) {
  switch (t) {
    case 'upn':         return 'UPN';
    case 'app_id':      return 'App ID';
    case 'app_name':    return 'App Name';
    case 'service_sid': return 'Service SID';
    case '*':           return 'Any (*)';
    default:            return t || '—';
  }
}

function previewMatchShape(target, actorType, actorValue) {
  const t = target?.trim() || '';
  const v = actorValue?.trim() || '';
  if (!t || !actorType || !v) return '';
  const targetLabel = t === '*' ? 'any mailbox' : t;
  if (actorType === '*') {
    return `Any actor (human, app, or service principal) may access ${targetLabel}.`;
  }
  const typeName = actorType === 'upn' ? 'UPN'
    : actorType === 'app_id' ? 'app GUID'
    : actorType === 'app_name' ? 'app name'
    : actorType === 'service_sid' ? 'service SID'
    : 'identity';
  if (v === '*') return `Any ${typeName} may access ${targetLabel}.`;
  return `Only this ${typeName} (${v}) may access ${targetLabel}.`;
}

function validateAddForm({ tenantId, targetMailboxUpn, actorType, actorValue, notes }) {
  if (!tenantId) return 'Tenant is required.';
  if (tenantId !== '*' && !GUID_REGEX.test(tenantId)) return "Tenant must be a GUID or '*'.";
  if (!['upn', 'app_id', 'app_name', 'service_sid', '*'].includes(actorType)) return 'Pick an actor type.';
  if (!targetMailboxUpn) return 'Target mailbox is required.';
  if (!actorValue) return 'Actor value is required.';
  if (actorType === '*' && actorValue !== '*') return "When actor type is '*', actor value must be '*'.";
  if (targetMailboxUpn === '*' && actorType === 'upn' && actorValue === '*') {
    return '(target=*, upn, *) would disable UPN-delegated suppression — rejected.';
  }
  if (targetMailboxUpn === '*' && actorType === '*' && actorValue === '*') {
    return '(*, *, *) would disable the rule — rejected.';
  }
  if (targetMailboxUpn !== '*' && targetMailboxUpn.includes('|')) return "Target mailbox cannot contain '|'.";
  if (actorValue !== '*' && actorValue.includes('|')) return "Actor value cannot contain '|'.";
  if (targetMailboxUpn !== '*') {
    if (targetMailboxUpn.length > 256) return 'Target mailbox exceeds 256 chars.';
    if (!UPN_REGEX.test(targetMailboxUpn)) return 'Target mailbox must be a UPN shape (foo@bar.tld).';
  }
  if (actorValue !== '*') {
    if (actorValue.length > 256) return 'Actor value exceeds 256 chars.';
    if (actorType === 'upn' && !UPN_REGEX.test(actorValue)) return 'Actor value for UPN must be a UPN shape.';
    if (actorType === 'app_id') {
      const stripped = actorValue.replace(/[{}]/g, '').toLowerCase();
      if (!GUID_REGEX.test(stripped)) return 'Actor value for app_id must be a GUID.';
    }
  }
  if (notes && notes.length > 2000) return 'Notes exceeds 2000 chars.';
  return null;
}

export default function MailboxPairAllowlistManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [allowlistRules, setAllowlistRules] = useState([]);   // [{ ruleId, ruleName }] — rules with the toggle on
  const [selectedRuleId, setSelectedRuleId] = useState('');   // which rule's entries are shown / written
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [actorTypeFilter, setActorTypeFilter] = useState('all');

  // Sentinel health (page-level pending-sync banner)
  const [health, setHealth] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [confirmReconcile, setConfirmReconcile] = useState(false);

  // Add form
  const [formTenantId, setFormTenantId] = useState('');
  const [formTarget, setFormTarget] = useState('');
  const [formTargetAny, setFormTargetAny] = useState(false);
  const [formActorType, setFormActorType] = useState('upn');
  const [formActorValue, setFormActorValue] = useState('');
  const [formActorValueAny, setFormActorValueAny] = useState(false);
  const [formNotes, setFormNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  // Inline edit (notes only)
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  // Delete confirmation
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Sort + pagination
  const [sortKey, setSortKey] = useState('addedUtc');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: Tenant, Target, Actor Type, Actor Value, Notes, Added By, Date, Actions
  const [colWidths, setColWidths] = useState([200, 240, 130, 240, 260, 160, 130, 100]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  // Load the rule selector options + tenants. Rules are discovered dynamically
  // from the skip_if_in_mailbox_pair_allowlist toggle; falls back to rule-008
  // only when the (admin-only) rules endpoint is unavailable.
  const loadMeta = async () => {
    try {
      const tenantData = await fetchTenants();
      setTenants(Array.isArray(tenantData) ? tenantData : []);
    } catch {
      // best-effort — tenant friendly names only
    }
    let rules = [];
    try {
      const all = await fetchDetectionRules();
      rules = (Array.isArray(all) ? all : [])
        .filter((r) => r.skipIfInMailboxPairAllowlist === true)
        .map((r) => ({ ruleId: r.ruleId, ruleName: r.ruleName || r.ruleId }));
    } catch {
      // 403 (non-admin) or transient — fall back below
    }
    if (rules.length === 0) rules = [RULE_008_FALLBACK];
    rules.sort((a, b) => (a.ruleName || '').localeCompare(b.ruleName || ''));
    setAllowlistRules(rules);
    setSelectedRuleId((prev) =>
      prev || rules.find((r) => r.ruleId === RULE_008_ID)?.ruleId || rules[0].ruleId
    );
  };

  // Load allowlist entries for the selected rule.
  const loadEntries = async (ruleId) => {
    if (!ruleId) return;
    setLoading(true);
    setError(null);
    try {
      const pairsData = await fetchMailboxPairAllowlist(ruleId, 'all');
      setRows(Array.isArray(pairsData) ? pairsData : []);
    } catch (err) {
      setError(err.message || 'Failed to load mailbox-pair allowlist');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    try {
      const h = await fetchMailboxPairAllowlistSentinelHealth();
      setHealth(h);
    } catch {
      // best-effort — leave previous health state
    }
  };

  useEffect(() => {
    loadMeta();
    loadHealth();
    const intervalId = setInterval(loadHealth, 30_000);
    return () => clearInterval(intervalId);
  }, []);

  // Reload entries whenever the selected rule changes.
  useEffect(() => {
    loadEntries(selectedRuleId);
    setCurrentPage(1);
  }, [selectedRuleId]);

  const tenantNameById = useMemo(() => {
    const map = {};
    tenants.forEach((t) => {
      if (t.TenantID) map[t.TenantID] = t.FriendlyName || t.TenantID;
    });
    return map;
  }, [tenants]);

  const sortedTenants = useMemo(
    () => [...tenants].sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || '')),
    [tenants]
  );

  const filtered = useMemo(() => {
    let r = rows;
    if (tenantFilter !== 'all') r = r.filter((x) => x.tenantId === tenantFilter || x.tenantId === '*');
    if (actorTypeFilter !== 'all') r = r.filter((x) => x.actorType === actorTypeFilter);
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      r = r.filter((x) =>
        (x.targetMailboxUpn && x.targetMailboxUpn.toLowerCase().includes(q)) ||
        (x.actorValue && x.actorValue.toLowerCase().includes(q)) ||
        (x.notes && x.notes.toLowerCase().includes(q)) ||
        (x.addedBy && x.addedBy.toLowerCase().includes(q))
      );
    }
    return r;
  }, [rows, tenantFilter, actorTypeFilter, filterQuery]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    result.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av === bv) return 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      return sortDir === 'asc' ? 1 : -1;
    });
    return result;
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { setCurrentPage(1); }, [filterQuery, tenantFilter, actorTypeFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = sorted.slice(startIdx, startIdx + pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIndicator = ({ k }) => {
    if (sortKey !== k) return <span className="text-gray-700 ml-1">{'↕'}</span>;
    return sortDir === 'asc' ? <ChevronUpIcon className="inline h-3 w-3 ml-1" /> : <ChevronDownIcon className="inline h-3 w-3 ml-1" />;
  };

  const handleActorTypeChange = (newType) => {
    setFormActorType(newType);
    // Plan §Validation rule 4: actor_type='*' forces actor_value='*'.
    if (newType === '*') {
      setFormActorValueAny(true);
      setFormActorValue('*');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const effectiveTarget    = formTargetAny ? '*' : formTarget.trim().toLowerCase();
    const effectiveActorValue = formActorValueAny ? '*' : formActorValue.trim().toLowerCase();
    const validationErr = validateAddForm({
      tenantId: formTenantId,
      targetMailboxUpn: effectiveTarget,
      actorType: formActorType,
      actorValue: effectiveActorValue,
      notes: formNotes,
    });
    if (validationErr) { setAddError(validationErr); return; }

    setAdding(true);
    setAddError(null);
    try {
      const created = await addMailboxPairAllowlistEntry({
        ruleId:           selectedRuleId,
        tenantId:         formTenantId,
        targetMailboxUpn: effectiveTarget,
        actorType:        formActorType,
        actorValue:       effectiveActorValue,
        notes:            formNotes.trim(),
      });
      setRows((prev) => {
        // Idempotent upsert — server returns same RowKey for re-add. Replace in-place if present.
        const ix = prev.findIndex((r) => r.partitionKey === created.partitionKey && r.rowKey === created.rowKey);
        if (ix >= 0) {
          const next = [...prev]; next[ix] = created; return next;
        }
        return [created, ...prev];
      });
      // Reset form
      setFormTenantId('');
      setFormTarget('');
      setFormTargetAny(false);
      setFormActorType('upn');
      setFormActorValue('');
      setFormActorValueAny(false);
      setFormNotes('');
      // Refresh sentinel health (pendingSync flag may have flipped)
      loadHealth();
    } catch (err) {
      setAddError(err.message || 'Failed to add entry');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (row) => {
    setEditError(null);
    setConfirmRemove(null);
    setEditingId(`${row.partitionKey}::${row.rowKey}`);
    setEditNotes(row.notes || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditNotes('');
    setEditError(null);
  };

  const handleSaveEdit = async (row) => {
    if (editNotes && editNotes.length > 2000) {
      setEditError('Notes exceeds 2000 chars.');
      return;
    }
    setSavingEdit(true);
    setEditError(null);
    try {
      const updated = await updateMailboxPairAllowlistEntry(row.partitionKey, row.rowKey, { notes: editNotes.trim() });
      setRows((prev) => prev.map((x) => (x.partitionKey === row.partitionKey && x.rowKey === row.rowKey ? { ...x, ...updated } : x)));
      cancelEdit();
      loadHealth();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleRemove = async (row) => {
    setRemoving(true);
    try {
      await removeMailboxPairAllowlistEntry(row.partitionKey, row.rowKey);
      setRows((prev) => prev.filter((x) => !(x.partitionKey === row.partitionKey && x.rowKey === row.rowKey)));
      setConfirmRemove(null);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to remove entry');
    } finally {
      setRemoving(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      await reconcileMailboxPairAllowlistSentinel(selectedRuleId);
      setConfirmReconcile(false);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to reconcile sentinel');
    } finally {
      setReconciling(false);
    }
  };

  const previewLine = previewMatchShape(
    formTargetAny ? '*' : formTarget,
    formActorType,
    formActorValueAny ? '*' : formActorValue
  );

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <EnvelopeIcon className="h-7 w-7 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-100">Mailbox-Pair Allowlist</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Per-tenant <span className="font-mono text-gray-400">(target_mailbox, actor)</span> suppression entries for{' '}
          <span className="font-mono text-gray-400">{allowlistRules.find((r) => r.ruleId === selectedRuleId)?.ruleName || selectedRuleId}</span>. The detection pipeline suppresses an
          aggregation only when <em>every</em> source event is covered by some entry. <span className="text-gray-400">Tenant, Target Mailbox, Actor Type, and Actor Value</span> are part of the row's identity (Tenant lives in the partition key; Target + Actor are hashed into the RowKey) and become immutable once the row is saved &mdash; <strong>only Notes are editable</strong>. To change any identity field, delete the entry and add a new one. Re-adding the same triple is an idempotent upsert.
        </p>
      </div>

      {/* Rule tabs — the page's primary context. Entries, the Add form, and
          Reconcile all act on the selected rule. */}
      <div className="mb-5 border-b border-gray-800">
        <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Detection rule">
          {allowlistRules.map((r) => {
            const active = r.ruleId === selectedRuleId;
            return (
              <button
                key={r.ruleId}
                role="tab"
                aria-selected={active}
                onClick={() => setSelectedRuleId(r.ruleId)}
                className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors ${
                  active
                    ? 'border-blue-500 text-blue-300 bg-gray-800/40'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                {r.ruleName}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sentinel health banner */}
      {health?.outboxPending && (
        <div className="mb-4 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg text-amber-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>
            Pending sync since {formatDate(health.outboxEnqueuedUtc)} ({health.outboxAttemptCount} attempt{health.outboxAttemptCount !== 1 ? 's' : ''}).
            The detection pipeline may not yet see recent changes. The retry timer runs every ~30 s.
          </span>
          {isAdmin && (
            <button
              onClick={() => setConfirmReconcile(true)}
              className="ml-auto underline hover:no-underline text-amber-200"
            >
              Reconcile now
            </button>
          )}
        </div>
      )}

      {/* Reconcile confirmation */}
      {confirmReconcile && (
        <div className="mb-4 p-3 bg-gray-900 border border-gray-700 rounded-lg text-sm flex items-center gap-3">
          <ArrowsRightLeftIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />
          <span className="text-gray-300">
            Force-bump the sentinel? This recomputes the version monotonically and clears any pending outbox row.
          </span>
          <button onClick={handleReconcile} disabled={reconciling} className="btn-primary px-3 py-1 text-xs ml-auto">
            {reconciling ? 'Reconciling…' : 'Confirm'}
          </button>
          <button onClick={() => setConfirmReconcile(false)} className="btn-secondary px-3 py-1 text-xs">Cancel</button>
        </div>
      )}

      {/* Add form */}
      {isAdmin ? (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4">
            Add Allowlist Entry
            <span className="text-gray-500 font-normal"> — {allowlistRules.find((r) => r.ruleId === selectedRuleId)?.ruleName || selectedRuleId}</span>
          </h2>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-start">
              {/* Tenant */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Tenant</label>
                <select
                  value={formTenantId}
                  onChange={(e) => setFormTenantId(e.target.value)}
                  className="input-field h-10 py-0"
                  required
                >
                  <option value="">— Tenant —</option>
                  <option value="*">All tenants (*)</option>
                  {sortedTenants.map((t) => (
                    <option key={t.TenantID} value={t.TenantID}>{t.FriendlyName || t.TenantID}</option>
                  ))}
                </select>
                <div className="mt-1 h-[22px]" aria-hidden="true" />
              </div>

              {/* Target mailbox */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Target Mailbox UPN</label>
                <input
                  type="text"
                  placeholder="e.g. alice@contoso.com"
                  value={formTargetAny ? '*' : formTarget}
                  onChange={(e) => setFormTarget(e.target.value)}
                  disabled={formTargetAny}
                  className={`input-field h-10 py-0 font-mono text-xs ${formTargetAny ? 'opacity-60 cursor-not-allowed' : ''}`}
                  required={!formTargetAny}
                />
                <label className="mt-1 flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formTargetAny}
                    onChange={(e) => setFormTargetAny(e.target.checked)}
                    className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                  />
                  Any mailbox (*)
                </label>
              </div>

              {/* Actor */}
              <div className="flex-[1.5] min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Actor</label>
                <div className="flex items-start gap-2">
                  <select
                    value={formActorType}
                    onChange={(e) => handleActorTypeChange(e.target.value)}
                    className="input-field h-10 py-0 w-32 flex-shrink-0"
                  >
                    <option value="upn">UPN</option>
                    <option value="app_id">App ID</option>
                    <option value="app_name">App Name</option>
                    <option value="service_sid">Service SID</option>
                    <option value="*">Any actor type (*)</option>
                  </select>
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      placeholder={
                        formActorType === 'upn'
                          ? 'e.g. bob@contoso.com'
                          : formActorType === 'app_id'
                          ? 'GUID'
                          : formActorType === 'app_name'
                          ? 'e.g. ScanProxy'
                          : formActorType === 'service_sid'
                          ? 'e.g. S-1-8-...'
                          : '*'
                      }
                      value={formActorValueAny ? '*' : formActorValue}
                      onChange={(e) => setFormActorValue(e.target.value)}
                      disabled={formActorValueAny || formActorType === '*'}
                      className={`input-field h-10 py-0 font-mono text-xs ${(formActorValueAny || formActorType === '*') ? 'opacity-60 cursor-not-allowed' : ''}`}
                      required={!formActorValueAny && formActorType !== '*'}
                    />
                    {formActorType !== '*' && (
                      <label className="mt-1 flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formActorValueAny}
                          onChange={(e) => {
                            setFormActorValueAny(e.target.checked);
                            if (e.target.checked) setFormActorValue('*');
                          }}
                          className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                        />
                        Any actor value (*)
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input
                  type="text"
                  placeholder="justification (required for wildcards)"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="input-field h-10 py-0"
                />
                <div className="mt-1 h-[22px]" aria-hidden="true" />
              </div>

              {/* Add button */}
              <div className="flex-shrink-0 lg:pt-5">
                <button
                  type="submit"
                  disabled={adding || !formTenantId || (!formTargetAny && !formTarget.trim()) || (!formActorValueAny && formActorType !== '*' && !formActorValue.trim())}
                  className="btn-primary flex items-center gap-2 whitespace-nowrap"
                >
                  {adding ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
                  Add Entry
                </button>
              </div>
            </div>

            {previewLine && (
              <p className="text-xs text-blue-300/80 flex items-start gap-1.5">
                <span className="font-semibold uppercase tracking-wider text-[10px]">Preview match shape:</span>
                <span className="text-gray-300">{previewLine}</span>
              </p>
            )}
            {/* Soft SID typo-catcher per handoff rule 5 — warn but never block. */}
            {formActorType === 'service_sid'
              && !formActorValueAny
              && formActorValue.trim()
              && !SID_REGEX.test(formActorValue.trim()) && (
              <p className="text-xs text-amber-300/80 flex items-start gap-1.5">
                <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>This doesn't look like a SID (expected pattern <span className="font-mono">S-1-…</span>). The entry will still be accepted — the matching tier takes the value verbatim — but double-check for a typo.</span>
              </p>
            )}
            <p className="text-xs text-gray-600">
              <strong>Tenant, Target Mailbox, Actor Type, and Actor Value</strong> are part of the row's identity and become immutable once saved. Only <strong>Notes</strong> can be edited later. To change any identity field, delete the entry and add a new one. Re-adding the same triple is an idempotent upsert (no duplicate row).
            </p>
          </form>
          {addError && <p className="mt-2 text-sm text-red-400">{addError}</p>}
        </div>
      ) : (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to add, edit, or remove entries.</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {editError && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{editError}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Filter by target, actor, notes..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="input-field sm:w-64"
          >
            <option value="all">All tenants</option>
            <option value="*">Cross-tenant (*) only</option>
            {sortedTenants.map((t) => (
              <option key={t.TenantID} value={t.TenantID}>{t.FriendlyName || t.TenantID}</option>
            ))}
          </select>
          <select
            value={actorTypeFilter}
            onChange={(e) => setActorTypeFilter(e.target.value)}
            className="input-field sm:w-44"
          >
            <option value="all">All actor types</option>
            <option value="upn">UPN</option>
            <option value="app_id">App ID</option>
            <option value="app_name">App Name</option>
            <option value="service_sid">Service SID</option>
            <option value="*">Any (*)</option>
          </select>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {isAdmin && (
            <button
              onClick={() => setConfirmReconcile(true)}
              disabled={reconciling}
              className="btn-secondary flex items-center gap-1.5 text-xs"
              title="Force-bump the sentinel (recovery)"
            >
              <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
              Reconcile sentinel
            </button>
          )}
          <span>{filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}</span>
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                {[
                  { label: 'Tenant',       sortKey: 'tenantId' },
                  { label: 'Target Mailbox', sortKey: 'targetMailboxUpn' },
                  { label: 'Actor Type',   sortKey: 'actorType' },
                  { label: 'Actor Value',  sortKey: 'actorValue' },
                  { label: 'Notes',        sortKey: null },
                  { label: 'Added By',     sortKey: 'addedBy' },
                  { label: 'Date',         sortKey: 'addedUtc' },
                  { label: 'Actions',      sortKey: null },
                ].map((col, colIdx) => (
                  <th
                    key={col.label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    onClick={col.sortKey ? () => toggleSort(col.sortKey) : undefined}
                    className={`${colIdx === 7 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none ${col.sortKey ? 'cursor-pointer hover:text-gray-300' : ''}`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{col.label}</span>
                    {col.sortKey && <SortIndicator k={col.sortKey} />}
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => { e.stopPropagation(); startResize(e, colIdx); }}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading allowlist...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <EnvelopeIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No allowlist entries</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery || tenantFilter !== 'all' || actorTypeFilter !== 'all'
                        ? 'Try adjusting your filters.'
                        : isAdmin ? 'Add an entry above to get started.' : 'No entries yet.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && paged.map((r) => {
                const compositeKey = `${r.partitionKey}::${r.rowKey}`;
                const isEditing = editingId === compositeKey;
                const isWildcardTarget = r.targetMailboxUpn === '*';
                const isWildcardActorValue = r.actorValue === '*';
                return (
                <tr key={compositeKey} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3 text-gray-200 truncate overflow-hidden" title={r.tenantId}>
                    {r.tenantId === '*' ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs">All tenants (*)</span>
                    ) : (
                      tenantNameById[r.tenantId] || r.tenantId
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-300 truncate overflow-hidden" title={r.targetMailboxUpn}>
                    {isWildcardTarget ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs font-sans">Any mailbox (*)</span>
                    ) : (
                      r.targetMailboxUpn
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={actorTypeTone(r.actorType)}>{actorTypeLabel(r.actorType)}</Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-300 truncate overflow-hidden" title={r.actorValue}>
                    {isWildcardActorValue ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs font-sans">
                        {r.actorType === '*' ? 'Any actor (*)' : 'Any value (*)'}
                      </span>
                    ) : (
                      r.actorValue
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 truncate overflow-hidden" title={r.notes || ''}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        className="input-field w-full text-xs py-1"
                        autoFocus
                      />
                    ) : (
                      r.notes || '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.addedBy || ''}>
                    {r.addedBy || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">
                    {formatDate(r.addedUtc)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {!isAdmin ? (
                      <span className="text-gray-700">{'—'}</span>
                    ) : isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleSaveEdit(r)}
                          disabled={savingEdit}
                          className="text-gray-600 hover:text-green-400 transition-colors p-1"
                          title="Save"
                        >
                          {savingEdit ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                        </button>
                        <button onClick={cancelEdit} disabled={savingEdit} className="text-gray-600 hover:text-gray-300 transition-colors p-1" title="Cancel">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : confirmRemove === compositeKey ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleRemove(r)} disabled={removing} className="btn-danger px-2 py-1 text-xs">
                          {removing ? 'Removing...' : 'Confirm'}
                        </button>
                        <button onClick={() => setConfirmRemove(null)} className="btn-secondary px-2 py-1 text-xs">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => startEdit(r)} className="text-gray-600 hover:text-blue-400 transition-colors p-1" title="Edit notes (Tenant / Target / Actor are immutable — delete and re-add to change them)">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => setConfirmRemove(compositeKey)} className="text-gray-600 hover:text-red-400 transition-colors p-1" title="Remove entry">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
